import React, { useState, useRef, useEffect, useCallback } from "react";
import "./Iospicker.css";

function ScrollWheel({ items, selectedIndex, onChange, width = 76, maxIndex }) {
  const ITEM_H = 46;
  const listRef = useRef(null);
  const timerRef = useRef(null);
  const isMounted = useRef(false);

  const getMax = () =>
    maxIndex !== undefined
      ? Math.min(maxIndex, items.length - 1)
      : items.length - 1;

  const scrollTo = useCallback((index, smooth = true) => {
    if (!listRef.current) return;
    listRef.current.scrollTo({
      top: index * ITEM_H,
      behavior: smooth ? "smooth" : "instant",
    });
  }, []);

  useEffect(() => {
    scrollTo(selectedIndex, false);
    const t = setTimeout(() => {
      isMounted.current = true;
    }, 300);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (isMounted.current) scrollTo(selectedIndex, true);
  }, [selectedIndex]);

  function snapToNearest() {
    if (!listRef.current) return;
    const snapped = Math.round(listRef.current.scrollTop / ITEM_H);
    const clamped = Math.max(0, Math.min(snapped, getMax()));
    scrollTo(clamped, true);
    onChange(clamped);
  }

  function handleScroll() {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(snapToNearest, 100);
  }

  return (
    <div className="sw-outer" style={{ width }}>
      <div className="sw-fade-top" />
      <div className="sw-selector" style={{ height: ITEM_H }} />
      <div className="sw-fade-bottom" />
      <div className="sw-list" ref={listRef} onScroll={handleScroll}>
        <div style={{ height: ITEM_H * 2, flexShrink: 0 }} />
        {items.map((item, i) => (
          <div
            key={i}
            className={`sw-item${i === selectedIndex ? " active" : ""}`}
            style={{ height: ITEM_H }}
            onClick={() => {
              const ci = Math.min(i, getMax());
              scrollTo(ci);
              onChange(ci);
            }}
          >
            {item}
          </div>
        ))}
        <div style={{ height: ITEM_H * 2, flexShrink: 0 }} />
      </div>
    </div>
  );
}

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const MONTHS_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function getDaysInMonth(year, monthIdx) {
  return new Date(year, monthIdx + 1, 0).getDate();
}
function buildDays(year, monthIdx) {
  return Array.from({ length: getDaysInMonth(year, monthIdx) }, (_, i) =>
    String(i + 1).padStart(2, "0"),
  );
}

export function IOSDatePicker({
  value,
  onChange,
  onClose,
  maxDate,
  minYear = 1924,
}) {
  const max = maxDate || new Date();
  const MAX_YEAR = max.getFullYear();
  const years = Array.from({ length: MAX_YEAR - minYear + 1 }, (_, i) =>
    String(minYear + i),
  );

  const initDate = value instanceof Date ? value : null;
  const initYIdx = initDate
    ? years.indexOf(String(initDate.getFullYear()))
    : years.length - 1;
  const initMIdx = initDate ? initDate.getMonth() : max.getMonth();
  const initDIdx = initDate ? initDate.getDate() - 1 : max.getDate() - 1;
  const [dYear, setDYear] = useState(
    initYIdx !== -1 ? initYIdx : years.length - 1,
  );
  const [dMonth, setDMonth] = useState(initMIdx);
  const [dDay, setDDay] = useState(initDIdx);
  const [daysList, setDaysList] = useState(() =>
    buildDays(initDate ? initDate.getFullYear() : max.getFullYear(), initMIdx),
  );
  const currentYear = parseInt(years[dYear]);

  const maxMonthIdx = currentYear === MAX_YEAR ? max.getMonth() : 11;
  const maxDayIdx =
    currentYear === MAX_YEAR && dMonth === max.getMonth()
      ? max.getDate() - 1
      : daysList.length - 1;

  useEffect(() => {
    const newDays = buildDays(currentYear, dMonth);
    setDaysList(newDays);
    const maxD =
      currentYear === MAX_YEAR && dMonth === max.getMonth()
        ? max.getDate() - 1
        : newDays.length - 1;
    if (dDay > maxD) setDDay(maxD);
  }, [dYear, dMonth]);

  function confirm() {
    const d = new Date(currentYear, dMonth, parseInt(daysList[dDay]));
    onChange && onChange(d);
    onClose && onClose();
  }

  function cancel() {
    onChange && onChange(null);
    onClose && onClose();
  }

  const previewText = `${daysList[dDay] || "01"} ${MONTHS_FULL[dMonth]} ${years[dYear]}`;

  return (
    <div
      className="ios-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose && onClose()}
    >
      <div className="ios-sheet">
        <div className="ios-handle" />
        <div className="ios-title">Date of Birth</div>
        <div className="ios-preview">{previewText}</div>

        <div className="ios-wheels-wrap">
          <ScrollWheel
            items={daysList}
            selectedIndex={dDay}
            onChange={setDDay}
            width={64}
            maxIndex={maxDayIdx}
          />
          <ScrollWheel
            items={MONTHS_SHORT}
            selectedIndex={dMonth}
            onChange={setDMonth}
            width={72}
            maxIndex={maxMonthIdx}
          />
          <ScrollWheel
            items={years}
            selectedIndex={dYear}
            onChange={setDYear}
            width={80}
          />
        </div>

        <div className="ios-actions">
          <button className="ios-btn ios-cancel" onClick={cancel}>
            Cancel
          </button>
          <button className="ios-btn ios-confirm" onClick={confirm}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

export function IOSTimePicker({
  value,
  onChange,
  onClose,
  title = "Time of Birth",
}) {
  const hours12 = Array.from({ length: 12 }, (_, i) =>
    String(i + 1).padStart(2, "0"),
  );
  const minutes = Array.from({ length: 60 }, (_, i) =>
    String(i).padStart(2, "0"),
  );
  const ampmList = ["AM", "PM"];

  const initT = value instanceof Date ? value : new Date();
  const [hIdx, setHIdx] = useState((initT.getHours() % 12 || 12) - 1);
  const [mIdx, setMIdx] = useState(initT.getMinutes());
  const [aIdx, setAIdx] = useState(initT.getHours() >= 12 ? 1 : 0);

  function confirm() {
    const d = new Date();
    let h = hIdx + 1;
    if (aIdx === 1) h = h === 12 ? 12 : h + 12;
    else h = h === 12 ? 0 : h;
    d.setHours(h, mIdx, 0, 0);
    onChange && onChange(d);
    onClose && onClose();
  }

  function cancel() {
    onChange && onChange(null);
    onClose && onClose();
  }

  const previewText = `${hours12[hIdx]}:${minutes[mIdx]} ${ampmList[aIdx]}`;

  return (
    <div
      className="ios-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose && onClose()}
    >
      <div className="ios-sheet">
        <div className="ios-handle" />
        <div className="ios-title">{title}</div>
        <div className="ios-preview">{previewText}</div>

        <div className="ios-wheels-wrap">
          <ScrollWheel
            items={hours12}
            selectedIndex={hIdx}
            onChange={setHIdx}
          />
          <span className="ios-sep">:</span>
          <ScrollWheel
            items={minutes}
            selectedIndex={mIdx}
            onChange={setMIdx}
          />
          <ScrollWheel
            items={ampmList}
            selectedIndex={aIdx}
            onChange={setAIdx}
            width={64}
          />
        </div>

        <div className="ios-actions">
          <button className="ios-btn ios-cancel" onClick={cancel}>
            Cancel
          </button>
          <button className="ios-btn ios-confirm" onClick={confirm}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
